/**
 * Tag Recovery (04 §2.3): a fallen ranger's marker - a rifle in the ground with a helmet on it -
 * stands somewhere in the approaches or the belt, a real detour off the straight line from the
 * start to the goal. It is placed last, from its own stream, so the rest of the map is the same
 * with or without it; `validate/tags.ts` holds it to the rules.
 */

import type { MissionDef, Rect } from '../sim/types';
import { NO_BUILD } from './builder';
import { FLAG } from './grid';
import { TAGS } from './tuning';
import type { GenCtx } from './types';
import { detourOf } from './validate/tags';

/** Cells that can be walked to from the start without touching anything solid, water or mines, by the scratch mask. */
function walkable(ctx: GenCtx): Uint8Array {
  const { width, height, cells } = ctx.map.mask;
  const shut = FLAG.solid | FLAG.water | FLAG.mine;
  const reached = new Uint8Array(width * height);
  const start = Math.floor(ctx.plan.player.z) * width + Math.floor(ctx.plan.player.x);
  const stack = [start];
  reached[start] = 1;
  while (stack.length > 0) {
    const cell = stack.pop() ?? 0;
    const x = cell % width;
    for (const n of [x > 0 ? cell - 1 : -1, x < width - 1 ? cell + 1 : -1, cell - width, cell + width]) {
      if (n < 0 || n >= reached.length || reached[n] !== 0 || ((cells[n] ?? shut) & shut) !== 0) continue;
      reached[n] = 1;
      stack.push(n);
    }
  }
  return reached;
}

/** Put the grave down and say whose it is; undefined when the mission has no tags to recover. */
export function placeFieldGrave(ctx: GenCtx, goalZone: Rect): MissionDef['dogTags'] {
  const who = ctx.params.tagRecovery;
  if (!who) return undefined;
  const rng = ctx.rng.fork('tag-recovery');
  const belt = ctx.bands[ctx.bands.length - 2];
  const drop = ctx.bands[0];
  if (!belt || !drop) return undefined;
  const reached = walkable(ctx);
  const avoid = NO_BUILD | FLAG.path;
  const [near, far] = TAGS.detour;
  for (let tries = 0; tries < TAGS.tries; tries++) {
    const x = Math.floor(rng.range(5, ctx.sizeX - 5)) + 0.5;
    const z = Math.floor(rng.range(belt.z0 + 2, drop.z0 - 2)) + 0.5;
    const off = detourOf(ctx.plan.player, goalZone, x, z);
    // A metre inside the limits, so rounding never tips it over them.
    if (off < near + 1 || off > far - 1) continue;
    if (reached[Math.floor(z) * ctx.map.mask.width + Math.floor(x)] === 0) continue;
    if (ctx.map.mask.hitsCircle(x, z, TAGS.clearing, avoid)) continue;
    const grave = ctx.map.add('fieldGrave', x, z, { yaw: Math.round(rng.range(-0.6, 0.6) * 100) / 100 });
    return { propId: grave.id, name: who.name };
  }
  ctx.map.problems.push('tag recovery: nowhere to stand the field grave');
  return undefined;
}
